import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { KanbanTask, TemplateType, PriorityTier, TaskScope, BoardConfig, DataScope, RecurrenceRangeType, HouseholdMember } from '@/types';
import { X, Calendar, Tag, Info, ListTodo, Plus, DollarSign, Repeat, Box, Users, Shield } from 'lucide-react';

interface TaskModalProps {
  task: KanbanTask | null;
  onClose: () => void;
  onCreate: (
    title: string,
    templateType: TemplateType,
    boardId: string,
    tags?: string[],
    scope?: TaskScope,
    recurrence?: KanbanTask['recurrence'],
    dueDate?: string,
    description?: string,
    priorityTier?: PriorityTier,
    cost?: number,
    consumption?: KanbanTask['consumption']
  ) => void;
  onUpdate: (taskId: string, updates: Partial<KanbanTask>) => void;
  onDelete: (taskId: string) => void;
  currentUserId: string;
  currentHouseholdId: string | null;
  availableBoards: BoardConfig[];
  householdMembers?: HouseholdMember[];
  initialTask?: { title?: string; description?: string; dueDate?: string; priorityTier?: PriorityTier };
}

export const TaskModal: React.FC<TaskModalProps> = ({
  task,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  currentUserId,
  currentHouseholdId,
  availableBoards,
  householdMembers,
  initialTask,
}) => {
  const { t } = useTranslation();
  const isEditing = !!task;

  // Form states (initialTask provides pre-fill for voice-to-task and other AI flows)
  const [title, setTitle] = useState(task?.title || initialTask?.title || '');
  const [description, setDescription] = useState(task?.description || initialTask?.description || '');
  const [templateType, setTemplateType] = useState<TemplateType>(task?.templateType || TemplateType.ACTION);
  const [priorityTier, setPriorityTier] = useState<PriorityTier>(task?.priorityTier || initialTask?.priorityTier || 'none');
  const [dueDate, setDueDate] = useState(task?.dueDate?.split('T')[0] || initialTask?.dueDate || ''); // YYYY-MM-DD
  const [tags, setTags] = useState<string[]>(task?.tags || []);
  const [newTag, setNewTag] = useState('');
  const [cost, setCost] = useState<number>(task?.consumption?.cost || 0);

  const DEFAULT_TASKS_BOARD: BoardConfig = {
  id: 'default-action-board',
  title: t('taskModal.defaultActionItems', 'Default Action Items'),
  templateType: TemplateType.ACTION,
  dataScope: 'PERSONAL',
  icon: 'list-todo',
  color: '#4f46e5',
  allowedStatuses: ['todo', 'in_progress', 'done'],
  visibility: 'public',
  isCollapsed: false,
  defaultGroupBy: 'status',
  activeFilters: [],
};

  // Scope state for creation
  const [scope, setScope] = useState<TaskScope>(
    task?.scope || (currentHouseholdId ? 'household' : 'personal')
  );

  // Recurrence for Maintenance tasks
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(task?.recurrence?.isEnabled || false);
  const [recurrenceType, setRecurrenceType] = useState<'fixed_interval' | 'monthly_date' | 'after_completion'>(
    'fixed_interval'
  );
  const [recurrenceInterval, setRecurrenceInterval] = useState(task?.recurrence?.interval || 1);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<RecurrenceRangeType | null>(
    task?.recurrence?.frequency ?? 'days'
  );
  const [monthlyDay, setMonthlyDay] = useState(task?.recurrence?.dayOfMonth || 1); // For 'every x of the month'

  // Rotation for recurring household tasks
  const [rotationEnabled, setRotationEnabled] = useState(task?.rotation?.enabled || false);
  const [rotationMemberIds, setRotationMemberIds] = useState<string[]>(task?.rotation?.memberIds || []);

  // Consumption for Grocery tasks
  const [consumptionQuantity, setConsumptionQuantity] = useState(task?.consumption?.quantity || 1);
  const [consumptionUnit, setConsumptionUnit] = useState(task?.consumption?.unit || 'unit');

  // Board selection for creation (always default for now)
  const defaultBoard = availableBoards.find(b => b.templateType === templateType) || DEFAULT_TASKS_BOARD;
  const [selectedBoardId, setSelectedBoardId] = useState(task?.boardId || defaultBoard.id);

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || '');
      setTemplateType(task.templateType);
      setPriorityTier(task.priorityTier || 'none');
      setDueDate(task.dueDate?.split('T')[0] || '');
      setTags(task.tags || []);
      setScope(task.scope);

      if (task.templateType === TemplateType.MAINTENANCE && task.recurrence) {
        setRecurrenceEnabled(task.recurrence.isEnabled);
        setRecurrenceInterval(task.recurrence.interval || 1);
        setRecurrenceFrequency(task.recurrence.frequency || 'days');
      } else {
        setRecurrenceEnabled(false);
        setRecurrenceInterval(1);
        setRecurrenceFrequency('days');
      }

      setRotationEnabled(task.rotation?.enabled || false);
      setRotationMemberIds(task.rotation?.memberIds || []);

      if (task.templateType === TemplateType.GROCERY && task.consumption) {
        setConsumptionQuantity(task.consumption.quantity || 1);
        setConsumptionUnit(task.consumption.unit || 'unit');
        setCost(task.consumption.cost || 0);
      } else {
        setConsumptionQuantity(1);
        setConsumptionUnit('unit');
        setCost(0);
      }
      setSelectedBoardId(task.boardId || defaultBoard.id);
    } else {
      // Reset for new task
      setTitle('');
      setDescription('');
      setTemplateType(TemplateType.ACTION);
      setPriorityTier('none');
      setDueDate('');
      setTags([]);
      setNewTag('');
      setCost(0);
      setScope(currentHouseholdId ? 'household' : 'personal'); // Default to household if available
      setRecurrenceEnabled(false);
      setRecurrenceType('fixed_interval');
      setRecurrenceInterval(1);
      setRecurrenceFrequency('days');
      setMonthlyDay(1);
      setRotationEnabled(false);
      setRotationMemberIds([]);
      setConsumptionQuantity(1);
      setConsumptionUnit('unit');
      setSelectedBoardId(defaultBoard.id);
    }
  }, [task, currentHouseholdId, availableBoards]);

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;

    const taskData: Partial<KanbanTask> = {
      title: title.trim(),
      description: description.trim(),
      templateType,
      priorityTier,
      dueDate: dueDate || undefined,
      tags: tags,
      scope,
    };

    if (templateType === TemplateType.MAINTENANCE) {
      if (recurrenceEnabled) {
        taskData.recurrence = {
          isEnabled: true,
          lastResetDate: task?.recurrence?.lastResetDate || new Date().toISOString(),
        };
        switch (recurrenceType) {
          case 'fixed_interval':
            taskData.recurrence = {
              ...taskData.recurrence,
              interval: recurrenceInterval,
              frequency: recurrenceFrequency,
              completionTrigger: false,
            };
            break;
          case 'monthly_date':
            taskData.recurrence = {
              ...taskData.recurrence,
              dayOfMonth: monthlyDay,
              frequency: 'months', // Set frequency to 'months' for this type
              completionTrigger: false,
            };
            break;
          case 'after_completion':
            taskData.recurrence = {
              ...taskData.recurrence,
              interval: recurrenceInterval,
              frequency: 'days', // Always days after completion
              completionTrigger: true,
            };
            break;
        }
      } else {
        taskData.recurrence = { isEnabled: false }; // Ensure recurrence is completely disabled if not enabled
      }
    } else {
      taskData.recurrence = { isEnabled: false }; // Ensure recurrence is reset for non-maintenance types
    }

    if (recurrenceEnabled && scope === 'household' && rotationEnabled && rotationMemberIds.length > 0) {
      taskData.rotation = {
        enabled: true,
        memberIds: rotationMemberIds,
        currentIndex: task?.rotation?.currentIndex ?? 0,
      };
    } else {
      taskData.rotation = undefined;
    }

    if (templateType === TemplateType.GROCERY) {
      taskData.consumption = {
        quantity: consumptionQuantity,
        remainingQuantity: consumptionQuantity, // Initially, remaining is full quantity
        unit: consumptionUnit,
        cost: cost,
      };
    } else {
      taskData.consumption = undefined; // Ensure consumption is reset
    }

    if (isEditing && task) {
      onUpdate(task.id, taskData);
    } else {
      onCreate(
        taskData.title!,
        taskData.templateType!,
        selectedBoardId, // Use selected board for new tasks
        taskData.tags,
        taskData.scope,
        taskData.recurrence,
        taskData.dueDate,
        taskData.description,
        taskData.priorityTier,
        taskData.consumption?.cost,
        taskData.consumption
      );
    }
    onClose();
  };

  const handleDelete = () => {
    if (task && window.confirm(t('taskModal.confirmDelete', 'Are you sure you want to delete this task?'))) {
      onDelete(task.id);
      onClose();
    }
  };

  const renderRecurrenceFields = () => (
    <div className="mb-4 bg-slate-50 dark:bg-slate-800 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
        <Repeat className="w-4 h-4 text-brand" />
        <input
          type="checkbox"
          checked={recurrenceEnabled}
          onChange={(e) => setRecurrenceEnabled(e.target.checked)}
          className="form-checkbox h-4 w-4 text-brand rounded"
        />
        {t('taskModal.enableRecurrence', 'Enable Recurrence')}
      </label>
      {recurrenceEnabled && (
        <div className="mt-3 space-y-3">
          {/* Recurrence Type Selection */}
          <div className="flex gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio" 
                value="fixed_interval"
                checked={recurrenceType === 'fixed_interval'}
                onChange={() => setRecurrenceType('fixed_interval')}
              />
              <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">{t('taskModal.everyX', 'Every X')} {recurrenceFrequency === 'days' ? t('taskModal.days', 'Days') : recurrenceFrequency === 'weeks' ? t('taskModal.weeks', 'Weeks') : recurrenceFrequency === 'months' ? t('taskModal.months', 'Months') : recurrenceFrequency === 'years' ? t('taskModal.years', 'Years') : ''}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio" 
                value="monthly_date"
                checked={recurrenceType === 'monthly_date'}
                onChange={() => setRecurrenceType('monthly_date')}
              />
              <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">{t('taskModal.everyXOfTheMonth', 'Every X of the Month')}</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="form-radio" 
                value="after_completion"
                checked={recurrenceType === 'after_completion'}
                onChange={() => setRecurrenceType('after_completion')}
              />
              <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">{t('taskModal.xDaysAfterLastCompletion', 'X Days After Last Completion')}</span>
            </label>
          </div>

          {/* Conditional Input Fields */}
          {recurrenceType === 'fixed_interval' && (
            <div className="flex gap-4">
              <input
                type="number"
                value={recurrenceInterval}
                onChange={(e) => setRecurrenceInterval(Math.max(1, parseInt(e.target.value) || 1))}
                placeholder={t('taskModal.interval', 'Interval')}
                className="flex-1 px-3 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
                min="1"
              />
              <select
                value={recurrenceFrequency || ''}
                onChange={(e) => setRecurrenceFrequency(e.target.value as RecurrenceRangeType | null)}
                className="flex-1 px-3 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <option value="days">{t('taskModal.daily', 'Daily')}</option>
                <option value="weeks">{t('taskModal.weekly', 'Weekly')}</option>
                <option value="months">{t('taskModal.monthly', 'Monthly')}</option>
                <option value="years">{t('taskModal.yearly', 'Yearly')}</option>
              </select>
            </div>
          )}

          {recurrenceType === 'monthly_date' && (
            <input
              type="number"
              value={monthlyDay}
              onChange={(e) => setMonthlyDay(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
              placeholder={t('taskModal.dayOfMonth', 'Day of the Month (1-31)')}
              className="w-full px-3 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
              min="1"
              max="31"
            />
          )}

          {recurrenceType === 'after_completion' && (
            <input
              type="number"
              value={recurrenceInterval}
              onChange={(e) => setRecurrenceInterval(Math.max(1, parseInt(e.target.value) || 1))}
              placeholder={t('taskModal.daysAfterLastCompletion', 'Days after last completion')}
              className="w-full px-3 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
              min="1"
            />
          )}
        </div>
      )}

      {recurrenceEnabled && scope === 'household' && householdMembers && householdMembers.length > 0 && (
        <div className="mt-4 border-t border-slate-200 dark:border-slate-600 pt-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
            <Users className="w-4 h-4 text-indigo-400" />
            <input
              type="checkbox"
              checked={rotationEnabled}
              onChange={(e) => setRotationEnabled(e.target.checked)}
              className="form-checkbox h-4 w-4 text-brand rounded"
            />
            {t('taskModal.enableRotation', 'Rotate Assignment')}
          </label>
          {rotationEnabled && (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-slate-500 dark:text-slate-400">{t('taskModal.rotationHint', 'Select members in rotation order:')}</p>
              <div className="flex flex-wrap gap-2">
                {householdMembers.map((m) => {
                  const selected = rotationMemberIds.includes(m.userId);
                  return (
                    <button
                      key={m.userId}
                      type="button"
                      onClick={() => setRotationMemberIds(prev =>
                        selected ? prev.filter(id => id !== m.userId) : [...prev, m.userId]
                      )}
                      className={`px-3 py-1 rounded-full text-xs font-bold border transition-all ${
                        selected
                          ? 'bg-indigo-500 text-white border-brand'
                          : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-indigo-400'
                      }`}
                    >
                      {m.name || m.userId}
                    </button>
                  );
                })}
              </div>
              {rotationMemberIds.length > 0 && (
                <p className="text-xs text-brand dark:text-indigo-400 font-medium">
                  {t('taskModal.rotationPreview', 'Order')}: {rotationMemberIds.map(id => householdMembers.find(m => m.userId === id)?.name || id).join(' → ')} → repeat
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderConsumptionFields = () => (
    <div className="mb-4 bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800">
      <label className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-200 mb-2">
        <Box className="w-4 h-4 text-emerald-500" />
        {t('taskModal.consumptionDetails', 'Consumption Details')}
      </label>
      <div className="flex gap-4 mt-3">
        <input
          type="number"
          value={consumptionQuantity}
          onChange={(e) => setConsumptionQuantity(Math.max(1, parseInt(e.target.value) || 1))}
          placeholder={t('taskModal.quantity', 'Quantity')}
          className="flex-1 px-3 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          min="1"
        />
        <input
          type="text"
          value={consumptionUnit}
          onChange={(e) => setConsumptionUnit(e.target.value)}
          placeholder={t('taskModal.unit', 'Unit (e.g., kg, pcs)')}
          className="flex-1 px-3 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div className="flex items-center gap-2 mt-3">
        <DollarSign className="w-4 h-4 text-emerald-500" />
        <input
          type="number"
          value={cost}
          onChange={(e) => setCost(parseFloat(e.target.value) || 0)}
          placeholder={t('taskModal.cost', 'Cost')}
          className="flex-1 px-3 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          min="0"
          step="0.01"
        />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-90 ease-out duration-300">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-800">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            {isEditing ? t('taskModal.editTask', 'Edit Task') : t('taskModal.createTask', 'Create New Task')}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Title */}
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
              {t('taskModal.title', 'Title')} <span className="text-red-500">*</span>
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('taskModal.titlePlaceholder', 'e.g., Plan weekend trip, Buy groceries')}
              className="w-full px-4 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
              required
            />
          </div>

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
              <Info className="inline-block w-4 h-4 mr-1 text-slate-400" />
              {t('taskModal.description', 'Description')}
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('taskModal.descriptionPlaceholder', 'Detailed notes about the task...')}
              rows={3}
              className="w-full px-4 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
            ></textarea>
          </div>

          {/* Template Type & Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="templateType" className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                <ListTodo className="inline-block w-4 h-4 mr-1 text-slate-400" />
                {t('taskModal.taskType', 'Task Type')}
              </label>
              <select
                id="templateType"
                value={templateType}
                onChange={(e) => setTemplateType(e.target.value as TemplateType)}
                className="w-full px-4 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <option value={TemplateType.ACTION}>{t('taskType.action', 'Action Item')}</option>
                <option value={TemplateType.GROCERY}>{t('taskType.grocery', 'Grocery Item')}</option>
                <option value={TemplateType.MAINTENANCE}>{t('taskType.maintenance', 'Maintenance')}</option>
              </select>
            </div>
            {templateType === TemplateType.ACTION && (
              <div>
                <label htmlFor="priorityTier" className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                  {t('taskModal.priority', 'Priority')}
                </label>
                <select
                  id="priorityTier"
                  value={priorityTier}
                  onChange={(e) => setPriorityTier(e.target.value as PriorityTier)}
                  className="w-full px-4 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="none">{t('priority.none', 'None')}</option>
                  <option value="P3">{t('priority.P3', 'P3 - Low')}</option>
                  <option value="P2">{t('priority.P2', 'P2 - Medium')}</option>
                  <option value="P1">{t('priority.P1', 'P1 - High')}</option>
                </select>
              </div>
            )}
          </div>

          {/* Scope Selection for new tasks only */}
          {!isEditing && (
            <div className="mb-4">
              <label htmlFor="scope" className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                <Shield className="inline-block w-4 h-4 mr-1 text-slate-400" />
                {t('taskModal.scope', 'Scope')}
              </label>
              <select
                id="scope"
                value={scope}
                onChange={(e) => setScope(e.target.value as TaskScope)}
                className="w-full px-4 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
                disabled={!currentHouseholdId} // Disable if no household is active
              >
                <option value="personal">{t('scope.personal', 'Personal')}</option>
                {currentHouseholdId && (
                  <option value="household">{t('scope.household', 'Household')}</option>
                )}
              </select>
              {!currentHouseholdId && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t('taskModal.householdRequired', 'Join a household to create shared tasks.')}
                </p>
              )}
            </div>
          )}


          {/* Dynamic Fields based on TemplateType */}
          {(templateType === TemplateType.MAINTENANCE || templateType === TemplateType.ACTION) && renderRecurrenceFields()}
          {templateType === TemplateType.GROCERY && renderConsumptionFields()}

          {/* Due Date */}
          <div className="mb-4">
            <label htmlFor="dueDate" className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
              <Calendar className="inline-block w-4 h-4 mr-1 text-slate-400" />
              {t('taskModal.dueDate', 'Due Date')}
            </label>
            <input
              id="dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-4 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          {/* Tags */}
          <div className="mb-6">
            <label htmlFor="tags" className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
              <Tag className="inline-block w-4 h-4 mr-1 text-slate-400" />
              {t('taskModal.tags', 'Tags')}
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-3 py-1 rounded-full text-xs font-medium"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder={t('taskModal.addTagPlaceholder', 'Add a tag')}
                className="flex-1 px-4 py-2 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-indigo-500 text-white rounded-xl hover:bg-brand transition-colors flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                <span className="sr-only">{t('taskModal.addTag', 'Add Tag')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-between items-center p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
          {isEditing && (
            <button
              onClick={handleDelete}
              className="px-6 py-3 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-colors text-sm font-bold uppercase tracking-wide"
            >
              {t('taskModal.delete', 'Delete')}
            </button>
          )}
          <button
            onClick={handleSubmit}
            className={`px-8 py-3 rounded-full shadow-lg transition-all text-sm font-bold uppercase tracking-wide ${
              isEditing ? 'bg-indigo-500 hover:bg-brand text-white' : 'bg-emerald-500 hover:bg-emerald-600 text-white ml-auto'
            }`}
          >
            {isEditing ? t('taskModal.saveChanges', 'Save Changes') : t('taskModal.create', 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};
